import { ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core';
import { provideTranslocoScope, TranslocoModule } from '@jsverse/transloco';
import { Contributor } from '../../../auth/user/contributor.model';

type CreditedRole = 'creator' | 'editor' | 'reviewer' | 'translator';

interface CreditRow {
  labelTranslationKey: string; // 'common.creator', 'common.reviewers' …
  names: { text?: string; translationKey?: string }[];
}

// In the order the work is done
const ROLES: CreditedRole[] = ['creator', 'editor', 'reviewer', 'translator'];

// What an anonymous person is called in each role
const ANONYMOUS_PERSON: Record<CreditedRole, string> = {
  creator: 'survey.question_credits.our_author',
  editor: 'survey.question_credits.our_editor',
  reviewer: 'survey.question_credits.our_editor',
  translator: 'survey.question_credits.our_translator',
};

/**
 * Who made a question, behind a "Credits" link. Shown only when an AI model has a credit
 * on the question; a question made by people alone shows nothing.
 *
 * Each role is a label followed by names, never a sentence, because the grammar of a sentence
 * built from the credits differs from language to language.
 */
@Component({
  selector: 'multisite-question-credits',
  templateUrl: './question-credits.component.html',
  styleUrls: ['./question-credits.component.scss'],
  imports: [TranslocoModule],
  providers: [provideTranslocoScope('survey', 'shows')],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuestionCreditsComponent {
  contributors = input<Contributor[] | undefined>();

  expanded = signal(false);

  hasAiCredit = computed(() => !!this.contributors()?.some(contributor => contributor.user_type === 'ai'));

  rows = computed<CreditRow[]>(() => {
    const contributors = this.contributors() ?? [];
    return ROLES
      .map(role => {
        const credited = contributors.filter(contributor => contributor.category === role);
        return {
          labelTranslationKey: 'common.' + role + (credited.length > 1 ? 's' : ''),
          names: credited.map(contributor => contributor.user?.name_full_honorific
            ? { text: contributor.user.name_full_honorific }
            : { translationKey: ANONYMOUS_PERSON[role] }),
        };
      })
      .filter(row => row.names.length);
  });

  toggle(): void {
    this.expanded.update(expanded => !expanded);
  }
}
